// This file is part of the Sleep History form tracking system.
// It provides CloudWatch RUM integration and dual-write to Postgres via tracking API.
// NOTE: Answer values are included in events. HIPAA compliance is enforced at the
// transport and storage layers (TLS in transit, encryption at rest).

import { recordRumError, recordRumEvent } from "../providers/RumProvider";

export enum SleepHistoryEventType {
  // Form Access Events
  FORM_LOADED = "shh.loaded",
  FORM_STARTED = "shh.started",
  WELCOME_START_CLICKED = "shh.welcome_start_clicked",

  // Form Navigation Events
  BLOCK_VIEWED = "shh.block_viewed",
  BLOCK_ANSWERED = "shh.block_answered",
  FORM_PROGRESS = "shh.progress",

  // Form Submission Events
  FORM_SUBMIT_CLICKED = "shh.submit_clicked",
  FORM_SUBMISSION_STARTED = "shh.submission_started",
  FORM_SUBMISSION_SUCCESS = "shh.submission_success",
  FORM_SUBMISSION_FAILED = "shh.submission_failed",

  // File Upload Events
  FILE_SELECTED = "shh.file_selected",
  FILE_UPLOAD_SUCCESS = "shh.file_upload_success",
  FILE_UPLOAD_ERROR = "shh.file_upload_error",
  FILE_REMOVED = "shh.file_removed",

  // Results Page Events
  RESULTS_PAGE_LOADED = "shh.results_page_loaded",
  RISK_SCORE_DISPLAYED = "shh.risk_score_displayed",

  // Booking Events
  BOOKING_FORM_OPENED = "shh.booking_form_opened",
  BOOKING_ATTEMPT_STARTED = "shh.booking_attempt_started",
  BOOKING_DATETIME_SELECTED = "shh.booking_datetime_selected",
  BOOKING_CONFIRMED = "shh.booking_confirmed",
  BOOKING_SKIPPED = "shh.booking_skipped",
  BOOKING_SUCCESS = "shh.booking_success",
  BOOKING_FAILED = "shh.booking_failed",

  // Error Events
  VALIDATION_ERROR = "shh.validation_error",
  NAVIGATION_ERROR = "shh.navigation_error",
  NETWORK_ERROR = "shh.network_error",
  SESSION_TIMEOUT = "shh.session_timeout",

  // Redirect Events
  REDIRECT_TO_RESULTS = "shh.redirect_to_results",
}

interface TrackingContext {
  assessmentId?: string;
  formId?: string;
  saxId?: string;
  practiceId?: string;
  tenantId?: string;
  redirectUri?: string;
}

export function getFormContext(): TrackingContext {
  try {
    const params = sessionStorage.getItem("searchParams");
    if (!params) return {};

    const searchParams = new URLSearchParams(params);
    return {
      formId: searchParams.get("formId") || undefined,
      practiceId: searchParams.get("practiceId") || undefined,
      redirectUri: searchParams.get("redirectUri") || undefined,
      saxId: searchParams.get("saxId") || undefined,
      tenantId: searchParams.get("tenantId") || undefined,
    };
  } catch {
    return {};
  }
}

function enrichWithFormContext<T extends Record<string, unknown>>(
  data: T,
): T & TrackingContext {
  const context = getFormContext();
  return { ...context, ...data };
}

/**
 * Payload sent to the sleepconnect tracking API.
 * Answer values are included. HIPAA compliance is enforced at transport (TLS)
 * and storage (encryption at rest) layers.
 */
interface ShhTrackEventPayload {
  event_type: string;
  form_id?: string;
  sax_id?: string;
  practice_id?: string;
  tenant_id?: string;
  block_id?: string;
  block_type?: string;
  block_index?: number;
  total_blocks?: number;
  progress_pct?: number;
  /** Answer value for the block that was just answered (shh.block_answered) */
  block_answer?: unknown;
  /** Full map of all form answers at the time of submission (shh.submission_started) */
  form_answers?: Record<string, unknown>;
  file_side?: string;
  file_mime_type?: string;
  file_size_bytes?: number;
  error_message?: string;
  risk_score?: string;
  step_number?: number;
  metadata?: Record<string, string | number | boolean>;
}

const TRACKING_API_URL =
  process.env.REACT_APP_TRACKING_API_URL ||
  (process.env.NODE_ENV === "production"
    ? "https://sleepconnect.mydreamconnect.com/api/forms/sleep-history/track-event"
    : process.env.NODE_ENV === "test"
      ? null
      : "https://staging.sleepconnect.mydreamconnect.com/api/forms/sleep-history/track-event");

/**
 * Send a single SHH event to both CloudWatch RUM and the Postgres tracking API.
 *
 * RUM write is synchronous (fire-and-forget inside recordRumEvent).
 * Postgres write is a best-effort async POST — failures are logged but never
 * thrown, so they cannot interrupt the user experience.
 *
 * Answer values are included in events. HIPAA compliance is handled at the
 * transport (TLS) and storage (encryption at rest) layers.
 */
export async function trackShhEvent(
  eventType: SleepHistoryEventType,
  payload: Omit<ShhTrackEventPayload, "event_type"> = {},
): Promise<void> {
  const context = getFormContext();

  const enriched: ShhTrackEventPayload = {
    event_type: eventType,
    form_id: payload.form_id || context.formId,
    practice_id: payload.practice_id || context.practiceId,
    sax_id: payload.sax_id || context.saxId,
    tenant_id: payload.tenant_id || context.tenantId,
    ...payload,
  };

  // Leg 1 — CloudWatch RUM (synchronous, existing path)
  recordRumEvent(eventType, {
    ...enriched,
    timestamp: new Date().toISOString(),
  });

  // Leg 2 — Postgres via tracking API (async, best-effort)
  // Skip entirely in test environment or if URL not configured
  if (!TRACKING_API_URL) return;

  try {
    await fetch(TRACKING_API_URL, {
      body: JSON.stringify(enriched),
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      method: "POST",
    });
  } catch (err) {
    console.warn("[SHH tracking] Postgres write failed (non-blocking):", err);
    recordRumError(err instanceof Error ? err : new Error(String(err)), { context: "shh_tracking_postgres_failed" });
  }
}

// ============================================================================

// ============================================================================
// Legacy tracking functions (still use RUM only)
// ============================================================================

export function trackFormLoaded(): void {
  recordRumEvent(SleepHistoryEventType.FORM_LOADED, enrichWithFormContext({}));
}

export function trackFormStarted(): void {
  recordRumEvent(SleepHistoryEventType.FORM_STARTED, enrichWithFormContext({}));
}

export function trackBlockViewed(
  blockId: string,
  blockName?: string,
  blockIndex?: number,
  totalBlocks?: number
): void {
  recordRumEvent(
    SleepHistoryEventType.BLOCK_VIEWED,
    enrichWithFormContext({ block_id: blockId, block_name: blockName, block_index: blockIndex, total_blocks: totalBlocks }),
  );
}

export function trackBlockAnswered(
  blockId: string,
  blockType: string,
  hasAnswer: boolean,
  blockKey?: string,
  answerValue?: unknown
): void {
  recordRumEvent(
    SleepHistoryEventType.BLOCK_ANSWERED,
    enrichWithFormContext({
      block_id: blockId,
      block_type: blockType,
      has_answer: hasAnswer,
      block_key: blockKey,
      block_answer: answerValue,
    }),
  );
}

export function trackFormProgress(
  currentBlock: number,
  totalBlocks: number,
): void {
  const progress = Math.round((currentBlock / totalBlocks) * 100);
  recordRumEvent(
    SleepHistoryEventType.FORM_PROGRESS,
    enrichWithFormContext({
      block_index: currentBlock,
      progress_pct: progress,
      total_blocks: totalBlocks,
    }),
  );
}

export function trackFormSubmissionStarted(context?: {
  answeredQuestions?: number;
  totalQuestions?: number;
  formAnswers?: Record<string, unknown>;
}): void {
  const { formAnswers, ...rest } = context || {};
  recordRumEvent(
    SleepHistoryEventType.FORM_SUBMISSION_STARTED,
    enrichWithFormContext({
      ...rest,
      ...(formAnswers !== undefined ? { form_answers: formAnswers } : {}),
    })
  );
}

export function trackFormSubmissionSuccess(context?: {
  transactionId?: string;
  formAnswers?: Record<string, unknown>;
}): void {
  const { formAnswers, ...rest } = context || {};
  recordRumEvent(
    SleepHistoryEventType.FORM_SUBMISSION_SUCCESS,
    enrichWithFormContext({
      ...rest,
      ...(formAnswers !== undefined ? { form_answers: formAnswers } : {}),
    })
  );
}

export function trackFormSubmissionFailed(
  errorMessage: string,
  formAnswers?: Record<string, unknown>
): void {
  recordRumEvent(
    SleepHistoryEventType.FORM_SUBMISSION_FAILED,
    enrichWithFormContext({
      error_message: errorMessage,
      ...(formAnswers !== undefined ? { form_answers: formAnswers } : {}),
    }),
  );
}

export function trackResultsPageLoaded(
  context?: Partial<TrackingContext>,
): void {
  recordRumEvent(
    SleepHistoryEventType.RESULTS_PAGE_LOADED,
    enrichWithFormContext(context || {}),
  );
}

export function trackRiskScoreDisplayed(
  score: string,
  context?: Partial<TrackingContext> & { riskLevel?: string },
): void {
  recordRumEvent(
    SleepHistoryEventType.RISK_SCORE_DISPLAYED,
    enrichWithFormContext({
      risk_level: context?.riskLevel,
      risk_score: score,
      ...context,
    }),
  );
}

export function trackBookingFormOpened(): void {
  recordRumEvent(
    SleepHistoryEventType.BOOKING_FORM_OPENED,
    enrichWithFormContext({}),
  );
}

export function trackBookingAttemptStarted(): void {
  recordRumEvent(
    SleepHistoryEventType.BOOKING_ATTEMPT_STARTED,
    enrichWithFormContext({}),
  );
}

export function trackBookingSuccess(): void {
  recordRumEvent(
    SleepHistoryEventType.BOOKING_SUCCESS,
    enrichWithFormContext({}),
  );
}

export function trackBookingFailed(errorMessage: string): void {
  recordRumEvent(
    SleepHistoryEventType.BOOKING_FAILED,
    enrichWithFormContext({ error_message: errorMessage }),
  );
}

export function trackValidationError(
  blockId: string,
  errorMessage: string,
): void {
  recordRumEvent(
    SleepHistoryEventType.VALIDATION_ERROR,
    enrichWithFormContext({
      block_id: blockId,
      error_message: errorMessage,
    }),
  );
}

export function trackNavigationError(errorMessage: string): void {
  recordRumEvent(
    SleepHistoryEventType.NAVIGATION_ERROR,
    enrichWithFormContext({ error_message: errorMessage }),
  );
}

// ============================================================================
// New typed wrappers that use trackShhEvent (dual-write)
// ============================================================================

/** Track welcome screen "Start" button click */
export function trackWelcomeStartClicked(): void {
  void trackShhEvent(SleepHistoryEventType.WELCOME_START_CLICKED);
}

/** Track file selected for upload (before validation) */
export function trackFileSelected(
  blockId: string,
  fileSide: "front" | "back",
  fileMimeType: string,
  fileSizeBytes: number
): void {
  void trackShhEvent(SleepHistoryEventType.FILE_SELECTED, {
    block_id: blockId,
    file_mime_type: fileMimeType,
    file_side: fileSide,
    file_size_bytes: fileSizeBytes,
  });
}

/** Track file upload validation success */
export function trackFileUploadSuccess(
  blockId: string,
  fileSide: "front" | "back" | "both"
): void {
  void trackShhEvent(SleepHistoryEventType.FILE_UPLOAD_SUCCESS, {
    block_id: blockId,
    file_side: fileSide,
  });
}

/** Track file upload validation error */
export function trackFileUploadError(
  blockId: string,
  fileSide: "front" | "back",
  errorMessage: string
): void {
  void trackShhEvent(SleepHistoryEventType.FILE_UPLOAD_ERROR, {
    block_id: blockId,
    error_message: errorMessage,
    file_side: fileSide,
  });
}

/** Track file removed */
export function trackFileRemoved(
  blockId: string,
  fileSide: "front" | "back"
): void {
  void trackShhEvent(SleepHistoryEventType.FILE_REMOVED, {
    block_id: blockId,
    file_side: fileSide,
  });
}


/** Track form submit button clicked (before async submission) */
export function trackFormSubmitClicked(): void {
  void trackShhEvent(SleepHistoryEventType.FORM_SUBMIT_CLICKED);
}

/** Track booking iframe becoming visible (Zoho booking opened) */
export function trackBookingIframeOpened(): void {
  void trackShhEvent(SleepHistoryEventType.BOOKING_FORM_OPENED);
}

/** Track booking skipped by patient */
export function trackBookingSkipped(): void {
  void trackShhEvent(SleepHistoryEventType.BOOKING_SKIPPED);
}

/** Track network error (fetch failures, timeouts) */
export function trackNetworkError(errorMessage: string): void {
  void trackShhEvent(SleepHistoryEventType.NETWORK_ERROR, {
    error_message: errorMessage,
  });
}

/** Track redirect to results page */
export function trackRedirectToResults(redirectUri: string): void {
  try {
    const url = new URL(redirectUri);
    void trackShhEvent(SleepHistoryEventType.REDIRECT_TO_RESULTS, {
      metadata: { destination_host: url.hostname },
    });
  } catch {
    void trackShhEvent(SleepHistoryEventType.REDIRECT_TO_RESULTS);
  }
}

